import { Component, EventEmitter, Input, OnChanges, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { ApolloCache, DocumentNode, OperationVariables, TypedDocumentNode } from '@apollo/client/core';
import { TranslateService } from '@ngx-translate/core';
import { DestroyNodeMutation, ValidationError } from '@queries/generated';
import { MessageService } from 'primeng/api';
import { ActionsMenuComponent } from '../actions-menu/actions-menu.component';

interface Node {
  __typename: string;
  id: number;
  gid: string;
}

type RefetchOption = { query: DocumentNode | TypedDocumentNode<any, OperationVariables>; variables: object };

@Component({
  selector: 'vnm-destroy-node-button',
  templateUrl: './destroy-node-button.component.html',
  styleUrls: ['./destroy-node-button.component.scss']
})
export class DestroyNodeButtonComponent implements OnInit, OnChanges {
  @Input() node: Node;
  @Output() done = new EventEmitter();
  @Output() update = new EventEmitter<ApolloCache<any>>();
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() header = 'app.confirmation.title';
  @Input() confirm = 'app.confirmation.delete';
  @Input() success = 'app.success.deleted';
  @Input() failure = 'app.failure.deleted';
  @Input() skipConfirmation = false;
  @Input() buttonClass = '';

  @Input() label = 'app.delete';
  @Input() icon = 'fa fa-fw fa-trash';
  @Input() iconOnly = false;

  @Input() acceptLabel = 'app.yes';
  @Input() acceptIcon = 'fa fa-fw fa-check';

  @Input() rejectLabel = 'app.no';
  @Input() rejectIcon = 'fa fa-fw fa-times';
  @Input() disabled = false;

  @Input() refetchQueries: string[] | RefetchOption[] = [];

  visible = false;
  buttonNgClass = {
    'p-button-text': this.iconOnly,
    [this.buttonClass]: true
  };

  constructor(
    private translateService: TranslateService,
    private destroyNodeMutation: DestroyNodeMutation,
    private messageService: MessageService,
    @Optional() private actionsMenu: ActionsMenuComponent
  ) {}

  ngOnInit() {
    if (this.actionsMenu) {
      this.actionsMenu.addItem({
        label: this.translateService.instant(this.label),
        disabled: this.disabled,
        icon: this.icon,
        command: ({ originalEvent }: { originalEvent: Event }) => this.open(originalEvent)
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.buttonClass || changes.iconOnly) {
      this.buttonNgClass = {
        'p-button-text': this.iconOnly,
        [this.buttonClass]: true
      };
    }
  }

  setVisible(value: boolean) {
    this.visible = value;
    this.visibleChange.emit(value);
  }

  open(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (this.skipConfirmation) {
      this.accept();
    } else {
      this.setVisible(true);
    }
  }

  reject() {
    this.setVisible(false);
  }

  accept() {
    this.destroyNodeMutation
      .mutate(
        { id: this.node.gid },
        {
          refetchQueries: this.refetchQueries,
          update: (store) => {
            this.update.next(store);
          }
        }
      )
      .subscribe(
        ({ data }) => {
          const { ok, errors } = data.destroyNode;
          if (ok) {
            this.ok();
          } else {
            this.validationErrors(errors);
          }
        },
        (error) => this.fail(error.message)
      );
  }

  private ok() {
    this.translateService.get(this.success).subscribe((summary) => {
      this.messageService.add({
        severity: 'success',
        summary
      });
      this.done.emit(this.node);
      this.setVisible(false);
    });
  }

  /**
   * It doesn't have the HTTP status code as an error, however it has the error message.
   */
  private validationErrors(errors: ValidationError[]) {
    this.messageService.add({
      severity: 'error',
      summary: errors.map((error: ValidationError) => error.message).join(' ')
    });

    this.setVisible(false);
  }

  private fail(message?: string) {
    this.translateService.get(message || this.failure).subscribe((summary) => {
      this.messageService.add({
        severity: 'error',
        summary
      });
    });
  }
}
